import { useLogout, useSession } from "@/features/auth";
import { CatalogDocTree, CatalogSwitcher } from "@/features/catalogs";
import { useHotkey } from "@/shared/hooks/use-hotkey";
import { IconButton, TooltipProvider } from "@/shared/ui";
import { Link, Outlet, useLocation, useMatches, useNavigate } from "@tanstack/react-router";

/**
 * Single-sidebar shell.
 *
 * ┌── sidebar ──────────┐
 * │ RTFM                │ brand
 * │ [catalog switcher]  │ visible only on /catalogs/:name/*
 * │ ─── body ─── (route-aware)
 * │   • /catalogs      → "Your catalogs" list
 * │   • /catalogs/:n   → doc tree
 * │   • /search        → section links
 * │   • /profile|/admin→ section links
 * │ ─── footer ───
 * │ 🔍  👤  ⚙  ⎋       icon buttons + sign-out
 * └─────────────────────┘
 */
export function AppShell() {
  const { data: user } = useSession();
  const catalogName = useCurrentCatalogName();

  return (
    <TooltipProvider>
      <div className="grid h-full grid-cols-[20rem_1fr]">
        <aside className="flex min-h-0 flex-col border-r border-[var(--border)] bg-[var(--surface-2)]">
          <div className="flex items-center justify-between px-4 py-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-bold tracking-tight text-[var(--fg)]"
            >
              <span className="inline-block size-2 rounded-full bg-[var(--accent)]" />
              RTFM
            </Link>
            {user && (
              <span className="text-[10px] uppercase tracking-wider text-[var(--fg-muted)]">
                {user.role}
              </span>
            )}
          </div>

          {catalogName && (
            <div className="px-3 pb-2">
              <CatalogSwitcher currentName={catalogName} />
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            <SidebarBody catalogName={catalogName} />
          </div>

          <SidebarFooter
            isAdmin={user?.role === "admin"}
            {...(user?.username ? { username: user.username } : {})}
          />
        </aside>
        <main className="min-w-0 overflow-auto bg-[var(--bg)]">
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  );
}

function useCurrentCatalogName(): string | undefined {
  const matches = useMatches();
  for (const m of matches) {
    const params = m.params as { name?: string };
    if (params?.name) return params.name;
  }
  return undefined;
}

function SidebarBody({ catalogName }: { catalogName: string | undefined }) {
  const loc = useLocation();
  const path = loc.pathname;

  if (catalogName) return <CatalogDocTree catalog={catalogName} />;

  if (path.startsWith("/search")) {
    return (
      <SectionNote
        title="Search"
        hint="Type in the main pane to query every catalog you can read."
      />
    );
  }
  if (path.startsWith("/admin")) {
    return (
      <SectionNote
        title="Admin"
        hint="Pick a user in the main pane to manage roles and catalog access."
      />
    );
  }
  return (
    <SectionNote
      title="Get started"
      hint="Pick a catalog from the main pane, or press ⌘J to open the switcher."
    />
  );
}

function SectionNote({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="px-2 py-1">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
        {title}
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-[var(--fg-muted)]">{hint}</p>
    </div>
  );
}

function SidebarFooter({
  isAdmin,
  username,
}: {
  isAdmin: boolean;
  username?: string;
}) {
  const loc = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();

  useHotkey("mod+k", () => navigate({ to: "/search" }));

  const on = (prefix: string) => loc.pathname.startsWith(prefix);

  async function onSignOut() {
    try {
      await logout.mutateAsync();
    } catch {
      /* server-side revoke failures still clear client state */
    }
    navigate({ to: "/login", search: {} });
  }

  return (
    <div className="flex items-center justify-between gap-1 border-t border-[var(--border)] px-2 py-2">
      <div className="flex items-center gap-0.5">
        <IconButton
          label="Catalogs"
          active={on("/catalogs") || loc.pathname === "/"}
          onClick={() => navigate({ to: "/catalogs" })}
        >
          <BookIcon />
        </IconButton>
        <IconButton
          label="Search (⌘K)"
          active={on("/search")}
          onClick={() => navigate({ to: "/search" })}
        >
          <SearchIcon />
        </IconButton>
        <IconButton
          label={`Profile${username ? ` — ${username}` : ""}`}
          active={on("/profile")}
          onClick={() => navigate({ to: "/profile" })}
        >
          <UserIcon />
        </IconButton>
        {isAdmin && (
          <IconButton
            label="Admin"
            active={on("/admin")}
            onClick={() => navigate({ to: "/admin" })}
          >
            <CogIcon />
          </IconButton>
        )}
      </div>
      <IconButton label="Sign out" onClick={onSignOut} disabled={logout.isPending}>
        <LogoutIcon />
      </IconButton>
    </div>
  );
}

/* ── icons (tiny inline SVGs, currentColor) ──────────────────────────── */

const svgProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
  "aria-hidden": true,
};

function BookIcon() {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: aria-hidden comes from svgProps
    <svg {...svgProps}>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
function SearchIcon() {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: aria-hidden comes from svgProps
    <svg {...svgProps}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
function UserIcon() {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: aria-hidden comes from svgProps
    <svg {...svgProps}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </svg>
  );
}
function CogIcon() {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: aria-hidden comes from svgProps
    <svg {...svgProps}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
function LogoutIcon() {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: aria-hidden comes from svgProps
    <svg {...svgProps}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
